import React, {useEffect, useRef, useState} from 'react';
import {ImageOptimized} from '@/_application/core/components/ui/image';
import {productPlaceholder} from '@/lib/placeholders';
import {FetchPriority} from '@/_application/core/types';
import {createPortal} from 'react-dom';
import {HOVER_ZOOM_CONSTANTS} from '@/_application/modules/products/constants/product.constants';

interface ImageSource {
    url: string;
    alt: string;
}

interface HoverZoomImageProps {
    imageSrc: ImageSource;
    width: number;
    height: number;
    priority?: boolean;
    fetchPriority?: FetchPriority;
    onClick?: () => void;
}

export const HoverZoomImage: React.FC<HoverZoomImageProps> = ({
    imageSrc,
    width,
    height,
    priority = false,
    fetchPriority = FetchPriority.AUTO,
    onClick,
}) => {
    const [lensPos, setLensPos] = useState({x: 0, y: 0});
    const [isHovering, setIsHovering] = useState(false);
    const [zoomPos, setZoomPos] = useState({left: 0, top: 0});
    const imgRef = useRef<HTMLButtonElement>(null);
    const {LENS_SIZE, ZOOM_FACTOR, ZOOM_OFFSET} = HOVER_ZOOM_CONSTANTS;

    const updateZoomPosition = () => {
        if (imgRef.current && globalThis.window !== undefined) {
            const rect = imgRef.current.getBoundingClientRect();
            setZoomPos({
                left: rect.right + ZOOM_OFFSET + window.scrollX,
                top: rect.top + window.scrollY,
            });
        }
    };

    const handleMouseEnter = () => setIsHovering(true);
    const handleMouseLeave = () => setIsHovering(false);
    const handleMouseMove = (e: React.MouseEvent) => {
        if (!imgRef.current) return;
        const rect = imgRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        setLensPos({
            x: Math.max(0, Math.min(x, rect.width)),
            y: Math.max(0, Math.min(y, rect.height)),
        });

        setZoomPos({
            left: rect.right + ZOOM_OFFSET + window.scrollX,
            top: rect.top + window.scrollY,
        });
    };

    useEffect(() => {
        if (!isHovering) return;

        const handleScroll = () => updateZoomPosition();
        const handleResize = () => updateZoomPosition();

        window.addEventListener('scroll', handleScroll);
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('scroll', handleScroll);
            window.removeEventListener('resize', handleResize);
        };
    }, [isHovering]);

    const zoomedImage = (() => {
        if (!imgRef.current) return null;

        const btnRect = imgRef.current.getBoundingClientRect();

        const lensX = Math.max(0, Math.min(lensPos.x - LENS_SIZE / 2, btnRect.width - LENS_SIZE));
        const lensY = Math.max(0, Math.min(lensPos.y - LENS_SIZE / 2, btnRect.height - LENS_SIZE));

        const zoomedSize = LENS_SIZE * ZOOM_FACTOR;

        const translateX = (lensX / (btnRect.width - LENS_SIZE)) * (btnRect.width - LENS_SIZE) * ZOOM_FACTOR;
        const translateY = (lensY / (btnRect.height - LENS_SIZE)) * (btnRect.height - LENS_SIZE) * ZOOM_FACTOR;

        return (
            <div
                className="hidden md:block border border-gray-300 overflow-hidden bg-white shadow-xl"
                style={{
                    width: zoomedSize,
                    height: zoomedSize,
                    position: 'absolute',
                    left: btnRect.right + ZOOM_OFFSET + window.scrollX,
                    top: btnRect.top + window.scrollY,
                    zIndex: 9999,
                }}>
                <div
                    style={{
                        width: btnRect.width * ZOOM_FACTOR,
                        height: btnRect.height * ZOOM_FACTOR,
                        transform: `translate(-${translateX}px, -${translateY}px)`,
                        transformOrigin: 'top left',
                        backgroundImage: `url(${imageSrc?.url ?? productPlaceholder})`,
                        backgroundSize: 'contain',
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'center',
                    }}
                />
            </div>
        );
    })();

    return (
        <div className="relative w-full">
            <div className="flex items-start justify-center gap-4 relative">
                <button
                    ref={imgRef}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                    onMouseMove={handleMouseMove}
                    className="relative"
                    style={{width, height}}>
                    <ImageOptimized
                        src={imageSrc?.url ?? productPlaceholder}
                        alt={imageSrc?.alt}
                        width={width}
                        height={height}
                        priority={priority}
                        fetchPriority={fetchPriority}
                        className="object-contain h-full"
                        onClick={onClick}
                        sizes="424px"
                    />

                    {isHovering && (
                        <div
                            className="absolute pointer-events-none flex justify-center items-center text-3xl shadow-200 bg-white opacity-40 rounded-lg"
                            style={{
                                width: LENS_SIZE,
                                height: LENS_SIZE,
                                left: Math.max(
                                    0,
                                    Math.min(
                                        lensPos.x - LENS_SIZE / 2,
                                        imgRef.current?.getBoundingClientRect().width! - LENS_SIZE,
                                    ),
                                ),
                                top: Math.max(
                                    0,
                                    Math.min(
                                        lensPos.y - LENS_SIZE / 2,
                                        imgRef.current?.getBoundingClientRect().height! - LENS_SIZE,
                                    ),
                                ),
                            }}>
                            +
                        </div>
                    )}
                </button>

                {isHovering && createPortal(zoomedImage, document.body)}
            </div>
        </div>
    );
};
